import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import { rescheduleOccurrence } from "../sessions/slice-extensions/occurrences-slice-extension";
import { OccurrenceStatus } from "../sessions/models/OccurrenceStatus";
import { CalendarItem } from "./models/CalendarItem";
import { OccurrenceDrag, occurrenceKey } from "./models/OccurrenceDrag";
import { toYmd } from "./utils/calendar-range";

/**
 * Drag an occurrence onto another day to reschedule it, keeping its time of day.
 * Uses native HTML5 drag — no dependency, and it leaves the chip's button
 * semantics (and therefore the keyboard path through the session detail) intact.
 * Touch devices don't fire these events; the session's own edit flow stays the
 * accessible route.
 */
export const useOccurrenceDrag = (onRescheduled: () => void): OccurrenceDrag => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [dragged, setDragged] = useState<CalendarItem | null>(null);
  const [dragOverYmd, setDragOverYmd] = useState<string | null>(null);
  const [isRescheduling, setIsRescheduling] = useState(false);

  const canDrag = (item: CalendarItem) => item.status !== OccurrenceStatus.Cancelled;

  const handleDragStart = (item: CalendarItem, event: React.DragEvent) => {
    if (!canDrag(item)) {
      event.preventDefault();
      return;
    }
    setDragged(item);
    event.dataTransfer.effectAllowed = "move";
    // Firefox only starts a drag once some payload is set.
    event.dataTransfer.setData("text/plain", occurrenceKey(item));
  };

  const handleDragEnd = () => {
    setDragged(null);
    setDragOverYmd(null);
  };

  const handleDayDragOver = (day: Date, event: React.DragEvent) => {
    if (!dragged) return;
    const ymd = toYmd(day);
    // Dropping back on the origin day is a no-op — don't invite it.
    if (ymd === dragged.date) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverYmd(ymd);
  };

  const handleDayDragLeave = (day: Date) => {
    setDragOverYmd((current) => (current === toYmd(day) ? null : current));
  };

  const handleDayDrop = async (day: Date, event: React.DragEvent) => {
    event.preventDefault();
    const item = dragged;
    setDragged(null);
    setDragOverYmd(null);
    if (!item) return;

    const newDate = toYmd(day);
    if (newDate === item.date) return;

    setIsRescheduling(true);
    try {
      await dispatch(
        rescheduleOccurrence({
          bookingId: item.bookingId,
          // Overrides are keyed by the series date, which does not move when the
          // occurrence does — sending the effective date would fail the second
          // time an occurrence is dragged.
          request: { occurrenceDate: item.originalDate, newDate, newTime: item.time },
        })
      ).unwrap();
      toast.success(t("calendar.drag.moved", { title: item.title }));
      onRescheduled();
    } catch (error) {
      toast.error(
        (error as { message?: string })?.message ??
          t("calendar.drag.failed", "That session could not be moved.")
      );
    } finally {
      setIsRescheduling(false);
    }
  };

  return {
    draggingKey: dragged ? occurrenceKey(dragged) : null,
    dragOverYmd,
    isRescheduling,
    canDrag,
    handleDragStart,
    handleDragEnd,
    handleDayDragOver,
    handleDayDragLeave,
    handleDayDrop,
  };
};
